import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';

import { useLanguage } from '../../contexts/LanguageContext';
import debugLogger from '../../utils/debug';
import {
  DOCUMENT_CROP_MARGIN_RATIO,
  buildPreviewCrop,
  isUsableRect,
  parseObjectPosition,
  planVideoCrop,
  validateOverlayMapping,
} from '../../utils/documentCrop';
import {
  estimateDocumentDistanceFromPreview,
  getDefaultDocumentQualityThresholds,
  scoreImageBlob,
  scoreVideoFrame,
} from '../../utils/cameraQuality';
import {
  applyBestDocumentCameraConstraints,
  getTrackCapabilities,
  stopMediaStream,
  tryRefocusCamera,
  trySetTorch,
} from '../../utils/cameraControls';
import {
  openDocumentCameraByDeviceId,
  selectBestDocumentCamera,
} from '../../utils/documentCameraSelection';
import { OVERLAY_TYPES } from './documentTypes';

const QUALITY_CHECK_INTERVAL_MS = 400;
const DOCUMENT_CAPTURE_SAMPLE_COUNT = 2;
const DOCUMENT_CAPTURE_SAMPLE_DELAY_MS = 80;
const COUNTDOWN_STEP_MS = 700;

const CARD_ASPECT_RATIO = 1.58;
const PASSPORT_ASPECT_RATIO = 1.35;

// Document capture wants the rear camera and as much resolution as the device
// will give us. There is deliberately no selfie constraint set in this file.
const DOCUMENT_VIDEO_CONSTRAINTS = {
  width: { ideal: 1920, min: 1280 },
  height: { ideal: 1080, min: 720 },
  frameRate: { ideal: 30, max: 30 },
  facingMode: { ideal: 'environment' },
};

const FALLBACK_VIDEO_CONSTRAINTS = {
  width: { ideal: 1280 },
  height: { ideal: 720 },
  facingMode: { ideal: 'environment' },
};

// ---------------------------------------------------------------- helpers

function nextAnimationFrame() {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => resolve());
    } else {
      resolve();
    }
  });
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function rectFromDomRect(rect) {
  return { x: rect.left, y: rect.top, width: rect.width, height: rect.height };
}

function drawFullFrameToCanvas(video, width, height) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) throw new Error('canvas_unavailable');

  context.drawImage(video, 0, 0, width, height);
  return canvas;
}

function cropRectToCanvas(source, crop) {
  const canvas = document.createElement('canvas');
  const drawWidth = Math.max(1, crop.width);
  const drawHeight = Math.max(1, crop.height);

  canvas.width = Math.max(1, Math.round(crop.width));
  canvas.height = Math.max(1, Math.round(crop.height));

  const context = canvas.getContext('2d');
  if (!context) throw new Error('canvas_unavailable');

  context.drawImage(source, crop.x, crop.y, drawWidth, drawHeight, 0, 0, canvas.width, canvas.height);
  return canvas;
}

function canvasToJpegBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (value) => {
        if (value) {
          resolve(value);
          return;
        }
        reject(new Error('encode_failed'));
      },
      'image/jpeg',
      0.9,
    );
  });
}

/** Best-first: passing quality, then sharpness, then least glare, then mid brightness. */
function compareCapturedFrameQuality(left, right) {
  const a = left.metadata.quality;
  const b = right.metadata.quality;

  if (a && b) {
    if (a.passed !== b.passed) return a.passed ? -1 : 1;
    if (a.sharpness !== b.sharpness) return b.sharpness - a.sharpness;
    if (a.glareRatio !== b.glareRatio) return a.glareRatio - b.glareRatio;
    return Math.abs(a.brightness - 132) - Math.abs(b.brightness - 132);
  }

  if (a) return -1;
  if (b) return 1;
  return 0;
}

// ---------------------------------------------------------------- component

/**
 * Live document capture for one side.
 *
 * Crop safety is the load-bearing invariant: if the overlay cannot be mapped
 * into source pixels the capture is REJECTED and the user retries. The full
 * camera frame is never used as a fallback.
 *
 * This component is document-only. It never requests a user-facing camera and
 * has no selfie path; the face image comes from Certage Face Liveness.
 */
function CameraCapture({
  side,
  overlayType,
  title,
  subtitle,
  frameCount = 1,
  debug = false,
  getPreferredDocumentCamera,
  onDocumentCameraSelected,
  onComplete,
  onCancel,
}) {
  const { t } = useLanguage();
  const copy = t.documentCapture;

  const videoRef = useRef(null);
  const overlayFrameRef = useRef(null);
  const streamRef = useRef(null);
  const trackRef = useRef(null);
  const qualityTimerRef = useRef(null);
  const mountedRef = useRef(true);
  const reviewUrlRef = useRef(null);

  const [status, setStatus] = useState('starting');
  const [errorKey, setErrorKey] = useState(null);
  const [hint, setHint] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [review, setReview] = useState(null);

  const isPassport = overlayType === OVERLAY_TYPES.PASSPORT;
  const aspectRatio = isPassport ? PASSPORT_ASPECT_RATIO : CARD_ASPECT_RATIO;

  // ------------------------------------------------------------- teardown

  const releaseReview = useCallback(() => {
    if (reviewUrlRef.current) {
      URL.revokeObjectURL(reviewUrlRef.current);
      reviewUrlRef.current = null;
    }
  }, []);

  const stopQualityLoop = useCallback(() => {
    if (qualityTimerRef.current) {
      clearInterval(qualityTimerRef.current);
      qualityTimerRef.current = null;
    }
  }, []);

  const stopCamera = useCallback(async () => {
    stopQualityLoop();

    const video = videoRef.current;
    if (video) {
      try {
        video.pause();
      } catch (error) {
        /* nothing useful to do */
      }
      video.srcObject = null;
    }

    if (streamRef.current) {
      await stopMediaStream(streamRef.current);
      streamRef.current = null;
    }
    trackRef.current = null;
  }, [stopQualityLoop]);

  // --------------------------------------------------------- quality loop

  const runQualityCheck = useCallback(() => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;

    try {
      const score = scoreVideoFrame(video, {
        maxWidth: 480,
        thresholds: getDefaultDocumentQualityThresholds(),
      });
      const distance = estimateDocumentDistanceFromPreview(video, {});

      if (distance && distance.tooClose) {
        setHint(copy.quality.moveFarther);
      } else if (distance && distance.tooFar) {
        setHint(copy.quality.moveCloser);
      } else if (score && score.glareRatio > getDefaultDocumentQualityThresholds().maxGlareRatio) {
        setHint(copy.quality.glare);
      } else if (score && score.sharpness < getDefaultDocumentQualityThresholds().minSharpness) {
        setHint(copy.quality.blur);
      } else if (score && score.brightness < getDefaultDocumentQualityThresholds().minBrightness) {
        setHint(copy.quality.tooDark);
      } else if (score && score.brightness > getDefaultDocumentQualityThresholds().maxBrightness) {
        setHint(copy.quality.tooBright);
      } else {
        setHint(copy.quality.good);
      }
    } catch (error) {
      // Quality hints are advisory only; never break the preview over them.
      setHint(null);
    }
  }, [copy]);

  // ------------------------------------------------------------ start camera

  const startCamera = useCallback(async () => {
    setStatus('starting');
    setErrorKey(null);

    try {
      let stream = null;

      const preferred =
        typeof getPreferredDocumentCamera === 'function' ? getPreferredDocumentCamera() : null;

      if (preferred && preferred.deviceId) {
        // openDocumentCameraByDeviceId resolves to a bare MediaStream, not a wrapper.
        stream = await openDocumentCameraByDeviceId({
          deviceId: preferred.deviceId,
          constraints: DOCUMENT_VIDEO_CONSTRAINTS,
          fallbackConstraints: FALLBACK_VIDEO_CONSTRAINTS,
        }).catch(() => null);
      }

      if (!stream) {
        const selection = await selectBestDocumentCamera({
          constraints: DOCUMENT_VIDEO_CONSTRAINTS,
          fallbackConstraints: FALLBACK_VIDEO_CONSTRAINTS,
          debug,
        });

        // The selector names every field selected*: selectedStream / selectedDeviceId
        // / selectedLabel. Reading .stream here silently discarded a working camera.
        stream = selection && selection.selectedStream ? selection.selectedStream : null;

        if (stream && selection.selectedDeviceId && typeof onDocumentCameraSelected === 'function') {
          onDocumentCameraSelected(selection.selectedDeviceId, selection.selectedLabel || null);
        }
      }

      if (!stream) throw new Error('document_camera_selection_failed');
      if (!mountedRef.current) {
        await stopMediaStream(stream);
        return;
      }

      streamRef.current = stream;
      const [track] = stream.getVideoTracks();
      trackRef.current = track || null;

      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        await new Promise((resolve) => {
          if (video.readyState >= 2) {
            resolve();
            return;
          }
          video.onloadedmetadata = () => resolve();
        });
        try {
          await video.play();
        } catch (error) {
          /* autoplay may be deferred; the preview still renders */
        }
      }

      if (track) {
        await applyBestDocumentCameraConstraints(track);
        const capabilities = getTrackCapabilities(track);
        setTorchAvailable(Boolean(capabilities && 'torch' in capabilities));
      }

      if (!mountedRef.current) return;

      setStatus('ready');
      stopQualityLoop();
      qualityTimerRef.current = setInterval(runQualityCheck, QUALITY_CHECK_INTERVAL_MS);
    } catch (error) {
      if (!mountedRef.current) return;

      const denied = error && (error.name === 'NotAllowedError' || error.name === 'SecurityError');

      // Label the log by what actually happened: 'denied' for a real permission
      // refusal, 'error' for everything else. The two were conflated, which sent
      // debugging after camera permissions when the camera had opened fine.
      debugLogger.cameraAccess(denied ? 'denied' : 'error', {
        step: 'document_capture_start',
        name: (error && error.name) || null,
        message: (error && error.message) || null,
      });
      setStatus(denied ? 'denied' : 'error');
      setErrorKey(denied ? 'denied' : 'cameraUnavailable');
    }
  }, [debug, getPreferredDocumentCamera, onDocumentCameraSelected, runQualityCheck, stopQualityLoop]);

  // Restart the camera whenever the requested side changes (front -> back).
  useEffect(() => {
    mountedRef.current = true;
    startCamera();

    return () => {
      mountedRef.current = false;
      stopCamera();
      releaseReview();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [side]);

  // ---------------------------------------------------------------- capture

  /**
   * Capture one frame cropped to the overlay.
   *
   * Throws when the overlay cannot be mapped to source pixels. The caller turns
   * that into a retry prompt. There is deliberately no whole-frame fallback.
   */
  const captureFrame = useCallback(
    async (index) => {
      const video = videoRef.current;
      if (!video) throw new Error('video_unavailable');

      // Measure layout and pixels in the same frame so they cannot drift.
      await nextAnimationFrame();

      const videoWidth = video.videoWidth;
      const videoHeight = video.videoHeight;
      if (!videoWidth || !videoHeight || video.readyState < 2) {
        throw new Error('video_not_ready');
      }

      const overlayEl = overlayFrameRef.current;
      const computedVideo = typeof getComputedStyle === 'function' ? getComputedStyle(video) : null;
      const objectFit = computedVideo && computedVideo.objectFit === 'contain' ? 'contain' : 'cover';
      const objectPosition = parseObjectPosition(
        (computedVideo && computedVideo.objectPosition) || '50% 50%',
      );

      const videoDomRect = rectFromDomRect(video.getBoundingClientRect());
      const overlayDomRect = overlayEl ? rectFromDomRect(overlayEl.getBoundingClientRect()) : null;
      const videoSourceSize = { width: videoWidth, height: videoHeight };

      const previewCrop = buildPreviewCrop({
        videoDomRect,
        overlayDomRect: overlayDomRect || { x: 0, y: 0, width: 0, height: 0 },
        videoSourceSize,
        objectFit,
        marginRatio: DOCUMENT_CROP_MARGIN_RATIO,
        objectPosition,
      });

      const mapping = validateOverlayMapping({
        videoSourceSize,
        videoDomRect,
        overlayDomRect,
        previewCrop,
      });

      // FAIL CLOSED. Never fall back to the full frame.
      if (!mapping.valid) {
        throw new Error('overlay_mapping_failed');
      }

      // planVideoCrop/planNativeCrop both expose the authoritative source-pixel
      // rect as sourceCropPx. There is no `cropPx`: reading that name yielded
      // undefined and surfaced as "Cannot read properties of undefined".
      const plan = planVideoCrop(previewCrop, videoSourceSize);

      // Fail by name rather than by TypeError if the plan shape ever drifts again.
      if (!plan || !isUsableRect(plan.sourceCropPx)) {
        throw new Error('crop_plan_unusable');
      }

      const referenceCanvas = drawFullFrameToCanvas(video, videoWidth, videoHeight);
      const canvas = cropRectToCanvas(referenceCanvas, plan.sourceCropPx);

      const blob = await canvasToJpegBlob(canvas);
      const quality = await scoreImageBlob(blob, getDefaultDocumentQualityThresholds()).catch(() => null);

      const capturedAt = new Date().toISOString();
      const safeCapturedAt = capturedAt.replace(/[:.]/g, '-');
      const file = new File([blob], `${side}-${safeCapturedAt}-${index}.jpg`, {
        type: 'image/jpeg',
      });

      return {
        file,
        metadata: {
          side,
          index,
          captured_at: capturedAt,
          width: canvas.width,
          height: canvas.height,
          crop_strategy: plan.strategy,
          quality: quality || null,
        },
      };
    },
    [side],
  );

  const handleCapture = useCallback(async () => {
    if (status !== 'ready') return;

    setStatus('capturing');
    setErrorKey(null);

    try {
      for (let n = 3; n >= 1; n -= 1) {
        if (!mountedRef.current) return;
        setCountdown(n);
        await wait(COUNTDOWN_STEP_MS);
      }
      setCountdown(null);

      const samples = [];
      const sampleCount = Math.max(DOCUMENT_CAPTURE_SAMPLE_COUNT, frameCount);

      for (let index = 0; index < sampleCount; index += 1) {
        samples.push(await captureFrame(index));
        if (index < sampleCount - 1) await wait(DOCUMENT_CAPTURE_SAMPLE_DELAY_MS);
      }

      if (!mountedRef.current) return;

      samples.sort(compareCapturedFrameQuality);
      const kept = samples.slice(0, frameCount);

      releaseReview();
      reviewUrlRef.current = URL.createObjectURL(kept[0].file);

      setReview({ frames: kept, previewUrl: reviewUrlRef.current });
      setStatus('review');
    } catch (error) {
      if (!mountedRef.current) return;

      // Only the failure reason is logged; never image data.
      debugLogger.log('Document capture failed', { side, reason: error && error.message });

      setCountdown(null);
      setStatus('ready');
      setErrorKey('captureFailed');
    }
  }, [captureFrame, frameCount, releaseReview, side, status]);

  const handleAccept = useCallback(() => {
    if (!review) return;

    const frames = review.frames;
    releaseReview();
    setReview(null);
    onComplete(side, frames);
  }, [onComplete, releaseReview, review, side]);

  const handleRetake = useCallback(() => {
    releaseReview();
    setReview(null);
    setStatus('ready');
  }, [releaseReview]);

  const handleRestartCamera = useCallback(async () => {
    await stopCamera();
    if (mountedRef.current) startCamera();
  }, [startCamera, stopCamera]);

  const handlePreviewTap = useCallback(
    (event) => {
      const track = trackRef.current;
      const video = videoRef.current;
      if (!track || !video || status !== 'ready') return;

      const rect = video.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      tryRefocusCamera(track, {
        x: (event.clientX - rect.left) / rect.width,
        y: (event.clientY - rect.top) / rect.height,
      });
    },
    [status],
  );

  const handleTorchToggle = useCallback(async () => {
    const track = trackRef.current;
    if (!track) return;

    const next = !torchOn;
    const applied = await trySetTorch(track, next);
    if (applied) setTorchOn(next);
  }, [torchOn]);

  // ------------------------------------------------------------------ render

  if (status === 'denied' || (status === 'error' && errorKey !== 'captureFailed')) {
    return (
      <Box className="docCapture__panel">
        <Typography variant="h2" className="docCapture__title">
          {status === 'denied' ? copy.permission.denied : copy.errors.cameraUnavailable}
        </Typography>
        <Typography className="docCapture__subtitle">{copy.permission.help}</Typography>

        <Box className="docCapture__actions">
          {/* Deliberately NOT "try again": the liveness page installs a
              MutationObserver that redirects on that label. */}
          <Button className="docCapture__primaryAction" onClick={handleRestartCamera}>
            {copy.capture.retryCamera}
          </Button>
          <Button className="docCapture__secondaryAction" onClick={onCancel}>
            {copy.capture.cancel}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="docCapture__camera">
      <Box className="docCapture__cameraHeader">
        <Typography className="docCapture__eyebrow">{title}</Typography>
        {subtitle ? <Typography className="docCapture__subtitle">{subtitle}</Typography> : null}
      </Box>

      <Box className="docCapture__stage" onClick={handlePreviewTap}>
        {review ? (
          <img className="docCapture__reviewImage" src={review.previewUrl} alt="" />
        ) : (
          <video
            ref={videoRef}
            className="docCapture__video"
            playsInline
            muted
            autoPlay
          />
        )}

        {!review ? (
          <Box
            ref={overlayFrameRef}
            className={`docCapture__overlayFrame ${isPassport ? 'is-passport' : 'is-card'}`}
            style={{ aspectRatio: String(aspectRatio) }}
          />
        ) : null}

        {countdown !== null ? (
          <Box className="docCapture__countdown">{countdown}</Box>
        ) : null}
      </Box>

      <Box className="docCapture__hintRow">
        {status === 'starting' ? (
          <Typography className="docCapture__hint">{copy.capture.starting}</Typography>
        ) : null}
        {status === 'ready' && hint ? (
          <Typography className="docCapture__hint">{hint}</Typography>
        ) : null}
        {status === 'capturing' && countdown !== null ? (
          <Typography className="docCapture__hint">{copy.capture.holdStill}</Typography>
        ) : null}
        {errorKey === 'captureFailed' ? (
          <Typography className="docCapture__hint is-error">{copy.errors.captureFailed}</Typography>
        ) : null}
        {!hint && status === 'ready' ? (
          <Typography className="docCapture__hint">{copy.capture.instruction}</Typography>
        ) : null}
      </Box>

      <Box className="docCapture__actions">
        {review ? (
          <>
            <Button className="docCapture__primaryAction" onClick={handleAccept}>
              {copy.capture.usePhoto}
            </Button>
            <Button className="docCapture__secondaryAction" onClick={handleRetake}>
              {copy.capture.retake}
            </Button>
          </>
        ) : (
          <>
            <Button
              className="docCapture__primaryAction"
              onClick={handleCapture}
              disabled={status !== 'ready'}
            >
              {copy.capture.capture}
            </Button>
            {torchAvailable ? (
              <Button className="docCapture__secondaryAction" onClick={handleTorchToggle}>
                {copy.capture.switchCamera}
              </Button>
            ) : null}
            <Button className="docCapture__secondaryAction" onClick={onCancel}>
              {copy.capture.cancel}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}

export default CameraCapture;
